// src/utils/mockDataXero.ts
// Step 7 — Xero / ERP Integration
// Connection config, chart of accounts mapping, sync queue and history

import type { SpendCategory } from './mockDataAnalytics'

// ── Types ─────────────────────────────────────────────────────────

export type SyncStatus  = 'QUEUED' | 'SYNCING' | 'SYNCED' | 'ERROR' | 'SKIPPED'
export type SyncItemType = 'INVOICE' | 'PURCHASE_ORDER' | 'PAYMENT'
export type AUTaxCode   = 'G10' | 'G11' | 'FRE' | 'EXEMPT'  // AU GST codes

export interface XeroConnection {
  connected: boolean
  tenantId?: string
  tenantName?: string
  tenantCountry?: string
  connectedAt?: string
  connectedByName?: string
  accessTokenExpiresAt?: string
  lastSyncAt?: string
  lastSyncStatus?: 'SUCCESS' | 'PARTIAL' | 'ERROR'
  lastSyncItemCount?: number
  lastSyncErrorCount?: number
  backendRequired: true    // always true — this is the reminder
}

export interface AccountMapping {
  id: string
  procurementCategory: SpendCategory
  categoryLabel: string
  xeroAccountCode: string
  xeroAccountName: string
  taxCode: AUTaxCode
  trackingCategory?: string
  isConfirmed: boolean
  aiSuggested: boolean
  aiReason?: string
}

export interface SyncQueueItem {
  id: string
  type: SyncItemType
  internalRef: string
  supplierName: string
  description: string
  amount: number          // ex GST
  gst: number
  status: SyncStatus
  xeroRef?: string        // e.g. bill number in Xero once synced
  xeroUrl?: string
  errorMessage?: string
  queuedAt: string
  syncedAt?: string
  mappedToAccount?: string
}

export interface SyncHistoryEntry {
  id: string
  runAt: string
  triggeredByName: string
  itemsAttempted: number
  itemsSucceeded: number
  itemsFailed: number
  status: 'SUCCESS' | 'PARTIAL' | 'ERROR'
  durationSeconds: number
  notes?: string
}

// ── Default connection (disconnected — backend not yet deployed) ───

export const XERO_CONNECTION: XeroConnection = {
  connected: false,
  backendRequired: true,
}

// ── Default account mappings (AI-generated, unconfirmed) ──────────
// These are sensible defaults for an AU civil/electrical contractor.
// Each mapping links a procurement category to a Xero expense account.

export const ACCOUNT_MAPPINGS: AccountMapping[] = [
  { id:'am-01', procurementCategory:'POWER_CABLE',      categoryLabel:'Power cable',         xeroAccountCode:'410', xeroAccountName:'Materials — Electrical cable',   taxCode:'G11', trackingCategory:'Materials',     isConfirmed:false, aiSuggested:true,  aiReason:'Electrical cable is a direct cost material — maps to a Materials sub-account under COGS. Full GST credit (G11) applies for a registered entity using goods in taxable supply.' },
  { id:'am-02', procurementCategory:'COMMS_CABLE',      categoryLabel:'Comms cable',         xeroAccountCode:'411', xeroAccountName:'Materials — Comms & fibre',       taxCode:'G11', trackingCategory:'Materials',     isConfirmed:false, aiSuggested:true,  aiReason:'Data and fibre cable used in ITS installations — same treatment as power cable, separate sub-account for reporting.' },
  { id:'am-03', procurementCategory:'CONDUIT_FITTINGS', categoryLabel:'Conduit & fittings',  xeroAccountCode:'412', xeroAccountName:'Materials — Civil supply',         taxCode:'G11', trackingCategory:'Materials',     isConfirmed:false, aiSuggested:true,  aiReason:'UPVC conduit and cable pits are civil supply items, direct cost materials for TMR projects.' },
  { id:'am-04', procurementCategory:'SIGNAL_EQUIPMENT', categoryLabel:'Signal equipment',    xeroAccountCode:'413', xeroAccountName:'Materials — Traffic signal equip.', taxCode:'G11', trackingCategory:'Materials',     isConfirmed:false, aiSuggested:true,  aiReason:'Signal heads, controllers and associated hardware — high-value items. Dedicated account aids project cost tracking and asset register where applicable.' },
  { id:'am-05', procurementCategory:'CIVIL_MATERIALS',  categoryLabel:'Civil materials',     xeroAccountCode:'414', xeroAccountName:'Materials — Civil (concrete/agg)', taxCode:'G11', trackingCategory:'Materials',     isConfirmed:false, aiSuggested:true,  aiReason:'Ready-mix concrete and aggregate — high volume, price-volatile. Separate account allows fuel surcharge analysis vs base material cost.' },
  { id:'am-06', procurementCategory:'STEEL',            categoryLabel:'Steel & fixings',     xeroAccountCode:'415', xeroAccountName:'Materials — Steel & fixings',      taxCode:'G11', trackingCategory:'Materials',     isConfirmed:false, aiSuggested:true,  aiReason:'Reinforcing steel and structural fixings — separate account recommended due to LME price volatility and audit trail requirements.' },
  { id:'am-07', procurementCategory:'PPE',              categoryLabel:'PPE & uniforms',      xeroAccountCode:'470', xeroAccountName:'PPE & safety equipment',           taxCode:'G11', trackingCategory:'Overhead',      isConfirmed:false, aiSuggested:true,  aiReason:'PPE is typically an overhead/admin cost rather than a direct job cost, though it can be job-allocated if MCB CE requires. Separate from materials.' },
  { id:'am-08', procurementCategory:'CONSUMABLES',      categoryLabel:'Consumables & tools', xeroAccountCode:'471', xeroAccountName:'Tools & consumables',              taxCode:'G11', trackingCategory:'Overhead',      isConfirmed:false, aiSuggested:true,  aiReason:'Small tools and consumables — overhead treatment unless project-specific. Review threshold with your accountant (items <$300 are generally expensed directly).' },
  { id:'am-09', procurementCategory:'FUEL',             categoryLabel:'Fuel',                xeroAccountCode:'460', xeroAccountName:'Fuel & fleet',                     taxCode:'G11', trackingCategory:'Overhead',      isConfirmed:false, aiSuggested:true,  aiReason:'Diesel and fleet fuel — ATO requires separate tracking for fuel tax credits (FTC). Ensure FTC claims are processed monthly.' },
  { id:'am-10', procurementCategory:'SUBCONTRACT',      categoryLabel:'Subcontractors',      xeroAccountCode:'420', xeroAccountName:'Subcontractors',                   taxCode:'G11', trackingCategory:'Subcontractors', isConfirmed:false, aiSuggested:true,  aiReason:'Subcontract costs are a direct cost, typically the largest single cost category. Withholding tax (PAYG-W) may apply if subcontractor is not registered for GST — flag for review.' },
]

// ── Sync queue (what would sync once connected) ───────────────────

export const SYNC_QUEUE: SyncQueueItem[] = [
  { id:'sq-001', type:'INVOICE', internalRef:'BCQ-INV-2026-0441',    supplierName:'Boral Construction Materials', description:'Ready-mix concrete Grade 40 — foundation pour TP-2026-001', amount:124,    gst:12.40,    status:'QUEUED', queuedAt:'2026-06-19T11:00:00Z', mappedToAccount:'Materials — Civil (concrete/agg)' },
  { id:'sq-002', type:'INVOICE', internalRef:'INV-OLX-2026-0441',    supplierName:'Prysmian Group (Olex)',        description:'3C+E 6mm² cable 850m — TP-2026-001 circuits C1–C4',          amount:14_280, gst:1_428,    status:'QUEUED', queuedAt:'2026-06-16T09:00:00Z', mappedToAccount:'Materials — Electrical cable' },
  { id:'sq-003', type:'INVOICE', internalRef:'SRD-INV-2026-0488',    supplierName:'Saferoads Queensland',         description:'Y16 reo, DIN plates × 6, UPVC conduit 540m — TP-2026-003', amount:6_076,  gst:607.60,   status:'QUEUED', queuedAt:'2026-06-03T10:00:00Z', mappedToAccount:'Materials — Civil supply' },
  { id:'sq-004', type:'INVOICE', internalRef:'BWD-INV-2026-0419',    supplierName:'Blackwoods Australia',         description:'PPE items — boots, hi-vis, gloves',                          amount:604,    gst:60.40,    status:'SKIPPED', queuedAt:'2026-06-20T14:00:00Z', errorMessage:'No PO linked — cannot post without job allocation. Resolve unmatched status first.', mappedToAccount:'PPE & safety equipment' },
  { id:'sq-005', type:'INVOICE', internalRef:'ALT-INV-2026-0388',    supplierName:'Aldridge Traffic Architects',  description:'Signal heads × 26, R900S controller — TP-2026-001',          amount:32_480, gst:3_248,    status:'SKIPPED', queuedAt:'2026-06-08T10:00:00Z', errorMessage:'QUANTITY_VARIANCE — invoice queried. Resolve 3-way match discrepancy before posting.', mappedToAccount:'Materials — Traffic signal equip.' },
  { id:'sq-006', type:'PURCHASE_ORDER', internalRef:'PO-TP003-010',  supplierName:'Downer EDI Works',             description:'Asphalt subcontract — Maroochydore Rd TP-2026-003',          amount:162_800,gst:16_280,   status:'QUEUED', queuedAt:'2026-06-15T09:00:00Z', mappedToAccount:'Subcontractors' },
]

// ── Sync history (empty — no syncs yet, backend not deployed) ─────

export const SYNC_HISTORY: SyncHistoryEntry[] = []
// Once backend is deployed and Xero is connected, this will populate.

// ── Tax code descriptions ─────────────────────────────────────────

export const TAX_CODE_META: Record<AUTaxCode, { label: string; description: string }> = {
  G10:    { label: 'G10 — Capital purchases',        description: 'GST on capital acquisitions — claim full ITC' },
  G11:    { label: 'G11 — Other purchases',          description: 'Standard GST purchase — claim full ITC (most common)' },
  FRE:    { label: 'FRE — GST-free',                 description: 'GST-free supply — no ITC claimable' },
  EXEMPT: { label: 'EXEMPT — Input-taxed',           description: 'Input-taxed supply — no ITC claimable' },
}

// ── Helpers ────────────────────────────────────────────────────────

export const QUEUED_TOTAL   = SYNC_QUEUE.filter(i => i.status === 'QUEUED').reduce((s,i) => s + i.amount + i.gst, 0)
export const SKIPPED_COUNT  = SYNC_QUEUE.filter(i => i.status === 'SKIPPED').length
export const CONFIRMED_MAPS = ACCOUNT_MAPPINGS.filter(m => m.isConfirmed).length
